import { Vector2, Vector3 } from "three";
import {
  IInputEventHandler,
  InputEventType,
  InputMultiPointerEvent,
  InputPointerType,
  IPinchArgs,
  ISwipeArgs,
} from "../utils/input-handler/interface";
import * as Utils from "../utils/utils";
import { Viewer } from "../viewer";

export interface IDrawingData {
  points: Vector2[];
  position: Vector2;
}

export interface IDrawing {
  isDrawingStarted: boolean;
  drawingData: IDrawingData;
  alternateSelection: boolean;
  start(
    resolve: any,
    reject: any,
    onProgress: (point: Vector2[], count: number) => void
  ): void;
  finish(): void;
  dispose(): void;
  onKeyDown(e: any): void;
  setVisibility(visible: boolean): void;
  // drawFromData(data: IDrawingData): void;
  // clone(): IDrawing;
}

export interface ICanvasDrawing extends IDrawing, IInputEventHandler {
  redraw(): void;
}

export class Polygon implements ICanvasDrawing {
  private _viewer: Viewer;
  readonly alternateSelection: boolean;
  private _canvas: HTMLCanvasElement;
  private _isDrawingStarted: boolean;
  private _pickPoints: { offset: Vector2; client: Vector2 }[];
  private _polygonPoints: Vector2[];
  private _position: Vector2;

  private _resolve: any;
  private _reject: any;
  private _onProgress: (point: Vector2[], count: number) => void;

  public get isDrawingStarted(): boolean {
    return this._isDrawingStarted;
  }

  public get drawingData(): IDrawingData {
    return { points: this._polygonPoints, position: this._position };
  }

  constructor(canvas: HTMLCanvasElement, viewer: Viewer) {
    this._canvas = canvas;
    this._isDrawingStarted = false;
    this._pickPoints = [];
    this._polygonPoints = [];
    this._viewer = viewer;
  }

  public setVisibility(visible: boolean): void {
    this._canvas.style.visibility = visible ? "visible" : "hidden";
  }

  public start(
    resolve: any,
    reject: any,
    onProgress?: (point: Vector2[], count: number) => void
  ): void {
    this._resolve = resolve;
    this._reject = reject;
    this._onProgress = onProgress;
    this._isDrawingStarted = true;
  }

  private reset() {
    this._isDrawingStarted = false;
    this._pickPoints = [];
    this._onProgress &&
      this._onProgress(
        this._pickPoints.map((p) => p.offset),
        this._pickPoints.length
      );
  }

  public dispose() {
    const ctx = this._canvas.getContext("2d");
    ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    this.reset();
    this._polygonPoints = [];
    this._reject && this._reject("Drawing has been cancelled.");
  }

  public restart() {
    this.dispose();
    this._isDrawingStarted = true;
  }

  public finish() {
    // this._canvas.style.pointerEvents = 'none';
    if (this._pickPoints.length > 2) {
      this._polygonPoints.push(...this._pickPoints.map((p) => p.client));
      if (
        this._polygonPoints[0].equals(
          this._polygonPoints[this._polygonPoints.length - 1]
        )
      ) {
        this._polygonPoints.pop();
      }
      this._position = this._pickPoints
        .reduce(
          (center, point) => center.addVectors(center, point.client),
          new Vector2(0, 0)
        )
        .multiplyScalar(1 / this._pickPoints.length);
      // this._pickPoints = [...this._polygonPoints, this._polygonPoints[0]];
      this.redraw();
    }

    this.reset();
    this._resolve && this._resolve(this);
  }

  public onKeyDown(event: any): void {
    if (event.key === "Enter") {
      this.finish();
    }

    if (event.key == "Delete") {
      this.dispose();
    }
  }

  public undo() {
    this._pickPoints.pop();
    this._onProgress &&
      this._onProgress(
        this._pickPoints.map((p) => p.offset),
        this._pickPoints.length
      );
    this.redraw();
  }

  public redraw(): void {
    const ctx = this._canvas.getContext("2d");
    ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    ctx.strokeStyle = "#FFFF00";
    ctx.beginPath();
    this._pickPoints[0] &&
      ctx.moveTo(this._pickPoints[0].offset.x, this._pickPoints[0].offset.y);
    for (let i = 1; i < this._pickPoints.length; ++i) {
      ctx.lineTo(this._pickPoints[i].offset.x, this._pickPoints[i].offset.y);
    }
    ctx.closePath();
    ctx.stroke();

    ctx.fillStyle = "#FF0000";
    for (let i = 0; i < this._pickPoints.length; ++i) {
      ctx.beginPath();
      ctx.arc(
        this._pickPoints[i].offset.x,
        this._pickPoints[i].offset.y,
        5,
        0,
        2 * Math.PI
      );
      ctx.closePath();
      ctx.fill();
    }
  }

  // public drawFromData(data: IDrawingData): void {
  //   this.reset();
  //   this._pickPoints = [...data.points];
  //   this.finish();
  // }

  public handlePointerEvent(
    pointer: { pointerType: InputPointerType; eventType: InputEventType },
    event: PointerEvent
  ): void {
    if (this.isDrawingStarted) {
      if (
        pointer.pointerType === InputPointerType.PRIMARY &&
        pointer.eventType === InputEventType.CLICK
      ) {
        if (
          this._viewer.poligonIndicesCount + this._pickPoints.length >=
          this._viewer.poligonMaxCount
        ) {
          const eventLimit = new CustomEvent("pointslimitreached", {
            detail: this._viewer.poligonMaxCount,
          });
          document.dispatchEvent(eventLimit);
          return;
        }

        this._pickPoints.push({
          client: new Vector2(event.clientX, event.clientY),
          offset: new Vector2(event.offsetX, event.offsetY),
        });
        this._onProgress &&
          this._onProgress(
            this._pickPoints.map((p) => p.offset),
            this._pickPoints.length
          );
        this.redraw();
      }
    }
  }

  public handleMultiplePointerEvent(
    eventType: InputMultiPointerEvent,
    args: ISwipeArgs | IPinchArgs
  ): void {
    throw new Error("Method not implemented.");
  }

  public getPickPointsLength() {
    return this._pickPoints.length;
  }

  // public clone() {
  //   return this;
  // }
}
